import { makeAutoObservable, runInAction } from "mobx";
import { globalModel } from "../global/GlobalModel";
import { SpaceApi } from "./SpaceApi";
import type {
  GetHomeDataResult,
  MembershipProduct,
  SpaceModuleConfig,
  SpaceProfile,
  SpaceTabItem,
  UserSubscriptionItem,
} from "./SpaceType";

const P = process.env.PUBLIC_URL || "";

function resolveTabIcon(
  slotActive: string | null | undefined,
  slotDeactive: string | null | undefined,
  moduleActive: string | null | undefined,
  moduleDeactive: string | null | undefined,
  fallbackActive: string,
  fallbackDeactive: string
): { active: string; deactive: string } {
  return {
    active: slotActive ?? moduleActive ?? fallbackActive,
    deactive: slotDeactive ?? moduleDeactive ?? fallbackDeactive,
  };
}

function normalizeStartupModule(raw: SpaceModuleConfig): SpaceModuleConfig {
  const homeRaw = raw.homeSubscribeCard;
  const homeSubscribeCard =
    homeRaw && typeof homeRaw === "object"
      ? {
          enabled: !!homeRaw.enabled,
          sortOrder:
            typeof homeRaw.sortOrder === "number" && homeRaw.sortOrder >= 1
              ? homeRaw.sortOrder
              : 1,
        }
      : undefined;
  return {
    ...raw,
    homeSubscribeCard,
    qaAllowPublic: raw.productKind === "qa" ? raw.qaAllowPublic === true : undefined,
    description: raw.description?.trim() ?? "",
    pricingMode: raw.pricingMode?.trim() ? raw.pricingMode : "retail",
    priceSpecs: Array.isArray(raw.priceSpecs) ? raw.priceSpecs : [],
    agreementMode: raw.agreementMode?.trim() ? raw.agreementMode : "none",
    agreementTemplateId: raw.agreementTemplateId?.trim() ? raw.agreementTemplateId : "",
    pcEntrance: raw.pcEntrance ?? { enabled: false, sortOrder: null },
    mobileEntrances: raw.mobileEntrances ?? {
      diamond: { enabled: false, sortOrder: 1, activeIconUrl: null, deactiveIconUrl: null },
      tabBar: { enabled: false, sortOrder: 1, activeIconUrl: null, deactiveIconUrl: null },
      mine: { enabled: false, sortOrder: 1, activeIconUrl: null, deactiveIconUrl: null },
    },
  };
}

function mergeModulesWithSubscriptions(
  modules: SpaceModuleConfig[],
  subs: UserSubscriptionItem[] | null
): MembershipProduct[] {
  if (subs == null || subs.length === 0) {
    return modules.map((m) => ({
      ...m,
      userSubscribed: false,
      daysRemaining: null,
      trialTierVisible: true,
      purchaseBlocked: false,
      purchaseBlockHint: null,
      upgradeEligible: false,
      upgradeSourceSpaceProductId: null,
      upgradeSourceDisplayName: null,
    }));
  }
  const byId = new Map(subs.map((s) => [s.spaceProductId, s]));
  return modules.map((m) => {
    const s = byId.get(m.spaceProductId);
    return {
      ...m,
      userSubscribed: s?.userSubscribed ?? false,
      daysRemaining: s?.daysRemaining ?? null,
      subscribedStrategyCount: s?.subscribedStrategyCount ?? null,
      strategyEntitlements: s?.strategyEntitlements ?? null,
      trialTierVisible: s?.trialTierVisible !== false,
      purchaseBlocked: s?.purchaseBlocked ?? false,
      purchaseBlockHint: s?.purchaseBlockHint ?? null,
      upgradeEligible: s?.upgradeEligible ?? false,
      upgradeSourceSpaceProductId: s?.upgradeSourceSpaceProductId ?? null,
      upgradeSourceDisplayName: s?.upgradeSourceDisplayName ?? null,
    };
  });
}

export function buildSpaceMobileTabs(modules: SpaceModuleConfig[]): SpaceTabItem[] {
  const homeIcons = resolveTabIcon(
    null,
    null,
    null,
    null,
    `${P}/icons/tab-home-active.svg`,
    `${P}/icons/tab-home.svg`
  );
  const home: SpaceTabItem = {
    key: "home",
    label: "首页",
    activeIconUrl: homeIcons.active,
    deactiveIconUrl: homeIcons.deactive,
    requiresLogin: false,
    fixed: true,
  };

  const dynamic = modules
    .filter((m) => m.mobileEntrances?.tabBar?.enabled)
    .sort(
      (a, b) =>
        (a.mobileEntrances?.tabBar?.sortOrder ?? 99) -
        (b.mobileEntrances?.tabBar?.sortOrder ?? 99)
    )
    .map((m) => {
      const icons = resolveTabIcon(
        m.mobileEntrances?.tabBar?.activeIconUrl,
        m.mobileEntrances?.tabBar?.deactiveIconUrl,
        m.activeIconUrl,
        m.deactiveIconUrl,
        `${P}/icons/tab-home-active.svg`,
        `${P}/icons/tab-home.svg`
      );
      return {
        key: m.spaceProductId,
        label: m.displayName,
        activeIconUrl: icons.active,
        deactiveIconUrl: icons.deactive,
        requiresLogin: true,
        fixed: false,
      } satisfies SpaceTabItem;
    });

  const mineIcons = resolveTabIcon(
    null,
    null,
    null,
    null,
    `${P}/icons/tab-mine-active.svg`,
    `${P}/icons/tab-mine.svg`
  );
  const mine: SpaceTabItem = {
    key: "mine",
    label: "我的",
    activeIconUrl: mineIcons.active,
    deactiveIconUrl: mineIcons.deactive,
    requiresLogin: true,
    fixed: true,
  };

  return [home, ...dynamic, mine];
}

export function isValidSpaceTab(tab: string | undefined, tabs: SpaceTabItem[]): boolean {
  if (!tab) {
    return false;
  }
  return tabs.some((t) => t.key === tab);
}

/** 路由 segment 是否可访问：TabBar 固定项 / 已配置产品（含仅金刚区入口） */
export function isValidSpaceRoute(
  tab: string | undefined,
  tabs: SpaceTabItem[],
  products: MembershipProduct[]
): boolean {
  if (isValidSpaceTab(tab, tabs)) {
    return true;
  }
  const id = tab?.trim();
  if (!id) {
    return false;
  }
  return products.some((p) => p.spaceProductId === id);
}

export class SpaceModel {
  spaceId = "";
  profile: SpaceProfile | null = null;
  modules: SpaceModuleConfig[] = [];
  membershipProducts: MembershipProduct[] = [];
  homeData: GetHomeDataResult | null = null;
  tabs: SpaceTabItem[] = buildSpaceMobileTabs([]);
  favorStatus: 0 | 1 = 0;
  loaded = false;
  loadError: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setSpaceId(id: string) {
    this.spaceId = id;
  }

  resetFavorState() {
    this.favorStatus = 0;
  }

  async loadFavorState() {
    const id = this.spaceId;
    if (!id || !globalModel.isLoggedIn) {
      runInAction(() => {
        this.favorStatus = 0;
      });
      return;
    }
    const s = await SpaceApi.getSpaceFavorState(id);
    runInAction(() => {
      this.favorStatus = s?.favorStatus === 1 ? 1 : 0;
    });
  }

  async refreshPublicProfile() {
    const id = this.spaceId;
    if (!id) {
      return;
    }
    const profileRes = await SpaceApi.getSpaceProfile(id);
    runInAction(() => {
      if (profileRes) {
        this.profile = profileRes;
      }
    });
  }

  async submitFavor(want: boolean): Promise<boolean> {
    const id = this.spaceId;
    if (!id) {
      return false;
    }
    const ok = await SpaceApi.setSpaceFavor(id, want);
    if (ok) {
      await this.loadFavorState();
      await this.refreshPublicProfile();
    }
    return ok;
  }

  async refreshSubscriptionMerge() {
    const id = this.spaceId;
    if (!id || this.modules.length === 0) {
      return;
    }
    if (!globalModel.isLoggedIn) {
      runInAction(() => {
        this.membershipProducts = mergeModulesWithSubscriptions(this.modules, null);
      });
      return;
    }
    const subRes = await SpaceApi.getSubscriptionDetail(id);
    runInAction(() => {
      if (typeof subRes === "string") {
        this.membershipProducts = mergeModulesWithSubscriptions(this.modules, null);
      } else {
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.modules,
          subRes.items ?? []
        );
      }
    });
  }

  async loadConfig() {
    const id = this.spaceId;
    runInAction(() => {
      this.loaded = false;
      this.loadError = null;
      this.modules = [];
      this.membershipProducts = [];
      this.homeData = null;
    });
    const [profile, modulesRaw, homeRes] = await Promise.all([
      SpaceApi.getSpaceProfile(id),
      SpaceApi.getStartupConfig(id),
      SpaceApi.getSpaceHomeData(id),
    ]);
    const modules = modulesRaw.map(normalizeStartupModule);
    runInAction(() => {
      if (this.spaceId !== id) {
        return;
      }
      this.profile = profile;
      this.modules = modules;
      this.homeData = homeRes;
      this.tabs = buildSpaceMobileTabs(modules);
      this.membershipProducts = mergeModulesWithSubscriptions(modules, null);
      this.loaded = true;
      if (!profile) {
        this.loadError = "空间不存在或暂不可访问";
      }
    });
  }
}
